import { z } from "zod";
import { computePayloadFingerprint } from "../fingerprint/fingerprint.js";
import { toCanonicalJsonString } from "../fingerprint/canonicalJson.js";
import { createHash } from "node:crypto";

export const originSystems = [
  "openai_mcp",
  "anthropic_mcp",
  "copilot_connector",
  "hubspot_wf",
  "intercom_fin",
  "zendesk_app",
  "sdk_direct",
  "admin_ui",
  "unknown"
] as const;

export type OriginSystem = (typeof originSystems)[number];

export const agentVendors = [
  "openai",
  "anthropic",
  "microsoft_copilot",
  "google",
  "custom"
] as const;

export type AgentVendor = (typeof agentVendors)[number];

export const riskClasses = ["low", "medium", "high"] as const;

export type RiskClass = (typeof riskClasses)[number];

export const expectedSideEffects = ["irreversible", "reversible", "none"] as const;

export type ExpectedSideEffect = (typeof expectedSideEffects)[number];

export const agentMetaSchema = z.object({
  vendor: z.enum(agentVendors).optional(),
  id: z.string().min(1).optional(),
  version: z.string().min(1).optional()
});

export type AgentMeta = z.infer<typeof agentMetaSchema>;

export const userDelegationSchema = z.object({
  actor_type: z.enum(["user", "service_account", "connector_inbound"]),
  actor_id: z.string().min(1)
});

export type UserDelegation = z.infer<typeof userDelegationSchema>;

export const requestContextSchema = z.object({
  parent_request_id: z.string().uuid().optional(),
  chain_id: z.string().min(1).optional()
});

export type RequestContext = z.infer<typeof requestContextSchema>;

export const toolMetaSchema = z.object({
  name: z.string().min(1),
  version: z.string().min(1).optional(),
  /** SHA-256 hex of the tool's JSON schema at invocation time, for drift detection */
  schema_hash: z.string().length(64).optional()
});

export type ToolMeta = z.infer<typeof toolMetaSchema>;

/** Additive v2 envelope fields that extend every authorizeRequestBody variant */
export const envelopeV2FieldsSchema = z.object({
  agent: agentMetaSchema.optional(),
  origin_system: z.enum(originSystems).optional(),
  user_delegation: userDelegationSchema.optional(),
  context: requestContextSchema.optional(),
  tool: toolMetaSchema.optional(),
  expected_side_effect: z.enum(expectedSideEffects).optional(),
  risk_class: z.enum(riskClasses).optional()
});

export type EnvelopeV2Fields = z.infer<typeof envelopeV2FieldsSchema>;

/** Thresholds used to derive risk_class from amount for financial actions */
const USD_MEDIUM_THRESHOLD = 100;
const USD_HIGH_THRESHOLD = 500;

const toUsd = (amount: number, currency: string): number => {
  const rates: Record<string, number> = { USD: 1, EUR: 1.08, GBP: 1.27, ILS: 0.27 };
  return amount * (rates[currency] ?? 1);
};

export const deriveRiskClass = (input: {
  action_type: string;
  amount?: number | null;
  currency?: string | null;
  expected_side_effect?: ExpectedSideEffect | null;
}): RiskClass => {
  if (input.expected_side_effect === "irreversible") {
    return "high";
  }
  if (
    input.action_type === "issue_refund" ||
    input.action_type === "issue_credit" ||
    input.action_type === "billing.credit.issue" ||
    input.action_type === "change_plan_or_price"
  ) {
    if (typeof input.amount === "number" && input.amount > 0) {
      const usd = toUsd(input.amount, input.currency ?? "USD");
      if (usd >= USD_HIGH_THRESHOLD) {
        return "high";
      }
      if (usd >= USD_MEDIUM_THRESHOLD) {
        return "medium";
      }
      return "low";
    }
    return "medium";
  }
  if (input.action_type === "contract.commitment.send") {
    if (typeof input.amount === "number" && input.amount > 0) {
      const usd = toUsd(input.amount, input.currency ?? "USD");
      if (usd >= USD_HIGH_THRESHOLD) {
        return "high";
      }
      if (usd >= USD_MEDIUM_THRESHOLD) {
        return "medium";
      }
      return "low";
    }
    return "medium";
  }
  if (input.action_type === "cancel_or_delete_account") {
    return "high";
  }
  return "medium";
};

/**
 * Computes the envelope fingerprint used as `payload_fingerprint` in permits.
 * Covers action_type + payload + resource_ref + tool.schema_hash (when present)
 * to bind the permit to the exact arguments and tool schema that was evaluated.
 */
export const computeEnvelopeFingerprint = (input: {
  action_type: string;
  payload: unknown;
  resource_ref: string;
  tool?: ToolMeta | null;
}): string => {
  const components = {
    action_type: input.action_type,
    payload: input.payload,
    resource_ref: input.resource_ref,
    tool_schema_hash: input.tool?.schema_hash ?? null
  };
  const canonical = toCanonicalJsonString(components);
  return createHash("sha256").update(canonical, "utf8").digest("hex");
};

/**
 * Convenience alias — wraps computePayloadFingerprint for callers
 * that only have the payload (legacy v1 path).
 */
export { computePayloadFingerprint };
